/**
 * Built-in defaults for helix deploy env and former-env API settings.
 * Single source of truth — import this (or `@onify/helix-core/default-env`) instead of hardcoding paths.
 *
 * Same pattern as `helix-config-paths.mjs` / `discover-customer-codes.mjs` — one Node-safe `.mjs` file.
 *
 * Keep aligned with `.env.example` and `Dockerfile` / `Dockerfile.proxy`.
 * API setting keys are snake_case only (`logout_url`, `error_url`, `internal_login_page`).
 *
 * `error_url` / `internal_login_page` are API defaults only — the app does not send them on login.
 * Tests may mock/override paths (e.g. `HELIX_INTERNAL_LOGIN_PAGE` in e2e helpers).
 *
 * @see docs/env-cannot-move-to-api.md
 * @see docs/env-to-api-settings-inventory.md
 */

/** @type {string} `HELIX_API_URL` */
export const DEFAULT_HELIX_API_URL = '/api/v2';

/** @type {string} `HELIX_BASE_URL` — Vite `base` + router history (stays env; api may mirror for redirects) */
export const DEFAULT_HELIX_BASE_URL = '/helix';

/** @type {string} `HELIX_LOGIN_URL` — nginx/Vite login location + app 401 redirect */
export const DEFAULT_HELIX_LOGIN_URL = '/login';

/** @type {string} `ONIFY_HUB_API_PATH` */
export const DEFAULT_ONIFY_HUB_API_PATH = '/api/v2';

/** @type {string} `ONIFY_HUB_APP_URL` */
export const DEFAULT_ONIFY_HUB_APP_URL = '/';

/** @type {string} API `logout_url` / was `HELIX_LOGOUT_URL` — app layouts use this */
export const DEFAULT_LOGOUT_URL = '/logout';

/**
 * API `error_url` / was `HELIX_ERROR_URL`.
 * api owns redirects to this path — not an app login query param.
 * Default assumes `/helix` deploy; root-path and tests should mock/override.
 * @type {string}
 */
export const DEFAULT_ERROR_URL = '/helix/error';

/**
 * API `internal_login_page` / was `HELIX_INTERNAL_LOGIN_PAGE`.
 * api owns redirects to this path — not an app login query param.
 * Default assumes `/helix` deploy; root-path and tests should mock/override.
 * @type {string}
 */
export const DEFAULT_INTERNAL_LOGIN_PAGE = '/helix/login/internal';

/**
 * Defaults: env-style keys for deploy vars; snake_case API keys for migrated auth paths.
 * @type {{
 *   HELIX_API_URL: string,
 *   HELIX_BASE_URL: string,
 *   HELIX_LOGIN_URL: string,
 *   ONIFY_HUB_API_PATH: string,
 *   ONIFY_HUB_APP_URL: string,
 *   logout_url: string,
 *   error_url: string,
 *   internal_login_page: string,
 * }}
 */
export const DEFAULT_ENV = {
  HELIX_API_URL: DEFAULT_HELIX_API_URL,
  HELIX_BASE_URL: DEFAULT_HELIX_BASE_URL,
  HELIX_LOGIN_URL: DEFAULT_HELIX_LOGIN_URL,
  ONIFY_HUB_API_PATH: DEFAULT_ONIFY_HUB_API_PATH,
  ONIFY_HUB_APP_URL: DEFAULT_ONIFY_HUB_APP_URL,
  logout_url: DEFAULT_LOGOUT_URL,
  error_url: DEFAULT_ERROR_URL,
  internal_login_page: DEFAULT_INTERNAL_LOGIN_PAGE,
};
